import { useMemo, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import type { Ambassador } from "@/modules/ambassadors/types";
import {
  Typography,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";

export const useAmbassadorUserColumns = (
  isLoadingLeadsCount: boolean,
  handleViewLeads: (id: string) => void
): ColumnDef<Ambassador & { leadsCount?: number }>[] => {
  const theme = useTheme();

  const renderName = useCallback(
    ({ row }: { row: any }) => (
      <Typography variant="body2" fontWeight={500}>
        {row.original.organization?.name || "N/A"}
      </Typography>
    ),
    []
  );

  const renderPhone = useCallback(
    ({ row }: { row: any }) => (
      <Typography variant="body2" fontWeight={500}>
        {row.original.phone || "N/A"}
      </Typography>
    ),
    []
  );

  const renderStatus = useCallback(
    ({ row }: { row: any }) => {
      const isAccepted = !!row.original.organizationId;
      return (
        <Chip
          label={isAccepted ? "Accepted" : "Pending"}
          color={isAccepted ? "success" : "default"}
        />
      );
    },
    []
  );

  const renderLeadsCount = useCallback(
    ({ row }: { row: any }) => {
      const ambassador = row.original;
      const cognitoId = ambassador.organization?.brokers?.[0]?.cognitoId;

      if (isLoadingLeadsCount && ambassador.organization) {
        return <CircularProgress size={16} />;
      }

      const hasLeads = (ambassador.leadsCount || 0) > 0;
      const isClickable = !!cognitoId && hasLeads;

      return (
        <Typography
          variant="body2"
          fontWeight={500}
          onClick={() => isClickable && handleViewLeads(cognitoId!)}
          sx={{
            cursor: isClickable ? "pointer" : "default",
            color: isClickable
              ? theme.nawy.colors.primitives.blue[500]
              : theme.nawy.colors.primitives.grey[700],
            textDecoration: isClickable ? "underline" : "none",
            "&:hover": isClickable
              ? { color: theme.nawy.colors.primitives.blue[600] }
              : {},
          }}
        >
          {ambassador.leadsCount || 0}
        </Typography>
      );
    },
    [isLoadingLeadsCount, handleViewLeads, theme]
  );

  return useMemo<ColumnDef<Ambassador & { leadsCount?: number }>[]>(
    () => [
      {
        accessorKey: "organization.name",
        header: "Name",
        cell: renderName,
      },
      {
        accessorKey: "phone",
        header: "Phone Number",
        cell: renderPhone,
      },
      {
        id: "status",
        header: "Status",
        cell: renderStatus,
      },
      {
        id: "leadsCount",
        header: "Leads Referred",
        cell: renderLeadsCount,
      },
    ],
    [renderName, renderPhone, renderStatus, renderLeadsCount]
  );
};
